import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { Categories, CategoryPublic, CategoryType } from '@cookspace-shared-contracts/core';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
    useCreateCategoryMutation,
    useDeleteCategoryMutation,
    useListCategoriesQuery,
    useReorderCategoriesMutation,
    useUpdateCategoryMutation
} from '@/api/categoriesApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { SortableList } from '@/components/SortableList';
import { Switch } from '@/components/Switch';
import { Textarea } from '@/components/Textarea';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const CATEGORY_TYPE_OPTIONS: Array<{ value: CategoryType; label: string }> = [
    { value: 'cuisine', label: 'Кухня' },
    { value: 'course_level', label: 'Уровень курса' },
    { value: 'event', label: 'Мероприятие' },
    { value: 'article', label: 'Рубрика статьи' }
];

const CATEGORY_TYPE_LABEL: Record<CategoryType, string> = {
    cuisine: 'Кухня',
    course_level: 'Уровень',
    event: 'Мероприятие',
    article: 'Статья'
};

interface FormState {
    name: string;
    slug: string;
    type: CategoryType;
    description: string;
    iconUrl: string;
    sortOrder: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    slug?: string;
    iconUrl?: string;
}

const EMPTY_FORM: FormState = {
    name: '',
    slug: '',
    type: 'cuisine',
    description: '',
    iconUrl: '',
    sortOrder: '0',
    isActive: true
};

export function CategoriesPage(): ReactElement {
    const [filterType, setFilterType] = useState<CategoryType | ''>('');
    const [filterActiveOnly, setFilterActiveOnly] = useState(false);

    const queryArg: Categories.GetCategoriesRequest = useMemo(() => {
        const arg: Categories.GetCategoriesRequest = {};
        if (filterType) {
            arg.type = filterType;
        }
        if (filterActiveOnly) {
            arg.isActive = true;
        }
        return arg;
    }, [filterType, filterActiveOnly]);

    const { data, isLoading, isError } = useListCategoriesQuery(queryArg);

    const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
    const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
    const [deleteCategory] = useDeleteCategoryMutation();
    const [reorderCategories] = useReorderCategoriesMutation();

    const [isModalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<CategoryPublic | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<CategoryPublic | null>(null);

    const items = data?.items ?? [];

    const openCreate = (): void => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, sortOrder: String(items.length) });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const openEdit = (category: CategoryPublic): void => {
        setEditing(category);
        setForm({
            name: category.name,
            slug: category.slug,
            type: category.type,
            description: category.description ?? '',
            iconUrl: category.iconUrl ?? '',
            sortOrder: String(category.sortOrder),
            isActive: category.isActive
        });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const validate = (): boolean => {
        const errors: FormErrors = {};
        if (!form.name.trim()) {
            errors.name = 'Название обязательно';
        }
        if (!form.slug.trim()) {
            errors.slug = 'Slug обязателен';
        } else if (!SLUG_REGEX.test(form.slug)) {
            errors.slug = 'Только латиница, цифры и дефисы';
        }
        if (form.iconUrl && !/^https?:\/\//.test(form.iconUrl)) {
            errors.iconUrl = 'URL должен начинаться с http(s)://';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSubmitError(null);
        if (!validate()) {
            return;
        }

        const sortOrderNum = Number(form.sortOrder);
        const body: Categories.CreateCategoryRequest = {
            name: form.name.trim(),
            slug: form.slug.trim(),
            type: form.type,
            isActive: form.isActive,
            sortOrder: Number.isFinite(sortOrderNum) ? sortOrderNum : 0
        };
        if (form.description.trim()) {
            body.description = form.description.trim();
        }
        if (form.iconUrl.trim()) {
            body.iconUrl = form.iconUrl.trim();
        }

        try {
            if (editing) {
                await updateCategory({ id: editing.id, body }).unwrap();
            } else {
                await createCategory(body).unwrap();
            }
            setModalOpen(false);
        } catch {
            setSubmitError('Не удалось сохранить категорию');
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteCategory({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch {
            setPendingDelete(null);
            setPageError('Не удалось удалить категорию');
        }
    };

    const handleToggleActive = async (
        category: CategoryPublic,
        next: boolean
    ): Promise<void> => {
        try {
            await updateCategory({
                id: category.id,
                body: { isActive: next }
            }).unwrap();
        } catch {
            setPageError('Не удалось изменить статус');
        }
    };

    const handleReorder = async (orderedIds: string[]): Promise<void> => {
        try {
            await reorderCategories({ orderedIds }).unwrap();
        } catch {
            setPageError('Не удалось изменить порядок');
        }
    };

    return (
        <div data-testid="categories-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Категории</h1>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="category-create-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать категорию
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded border border-gray-200 bg-white p-3">
                <div className="w-56">
                    <Select
                        label="Тип"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as CategoryType | '')}
                        placeholder="Все типы"
                        options={CATEGORY_TYPE_OPTIONS}
                        data-testid="category-filter-type"
                    />
                </div>
                <div className="pt-5">
                    <Switch
                        checked={filterActiveOnly}
                        onChange={setFilterActiveOnly}
                        label="Только активные"
                        testId="category-filter-active"
                    />
                </div>
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="categories-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="categories-error">
                    Не удалось загрузить категории
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="categories-table"
                >
                    <div className="grid grid-cols-[40px_80px_minmax(0,1fr)_minmax(0,1fr)_120px_60px_80px_80px_140px] gap-2 border-b border-gray-200 bg-gray-50 px-2 py-2 text-xs font-medium uppercase text-gray-600">
                        <span />
                        <span>ID</span>
                        <span>Название</span>
                        <span>Slug</span>
                        <span>Тип</span>
                        <span>Иконка</span>
                        <span>Порядок</span>
                        <span>Активна</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Категории не найдены
                        </div>
                    )}

                    <SortableList
                        items={items}
                        onReorder={handleReorder}
                        testId="categories-sortable"
                        renderItem={(category) => (
                            <div
                                className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)_120px_60px_80px_80px_140px] items-center gap-2 border-b border-gray-100 px-2 py-2 text-sm last:border-b-0"
                                data-testid={`category-row-${category.id}`}
                            >
                                <span className="truncate font-mono text-xs text-gray-500">
                                    {category.id.slice(0, 8)}
                                </span>
                                <span className="truncate text-gray-900">{category.name}</span>
                                <span className="truncate text-gray-600">{category.slug}</span>
                                <span>
                                    <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                        {CATEGORY_TYPE_LABEL[category.type]}
                                    </span>
                                </span>
                                <span>
                                    {category.iconUrl !== null && category.iconUrl !== '' ? (
                                        <img
                                            src={category.iconUrl}
                                            alt=""
                                            className="h-8 w-8 rounded object-cover"
                                        />
                                    ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </span>
                                <span className="text-gray-700">{category.sortOrder}</span>
                                <span>
                                    <Switch
                                        checked={category.isActive}
                                        onChange={(next) => handleToggleActive(category, next)}
                                        testId={`category-active-${category.id}`}
                                    />
                                </span>
                                <span className="flex justify-end gap-1">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(category)}
                                        data-testid={`category-edit-${category.id}`}
                                        className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-label="Редактировать"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPendingDelete(category)}
                                        data-testid={`category-delete-${category.id}`}
                                        className="rounded p-1.5 text-red-600 transition hover:bg-red-50"
                                        aria-label="Удалить"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </span>
                            </div>
                        )}
                    />
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? 'Редактировать категорию' : 'Новая категория'}
                testId="category-modal"
            >
                <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-testid="category-form"
                >
                    <Input
                        label="Название"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        error={formErrors.name}
                        data-testid="category-form-name"
                        autoFocus
                    />
                    <Input
                        label="Slug"
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                        placeholder="example-slug"
                        error={formErrors.slug}
                        data-testid="category-form-slug"
                    />
                    <Select
                        label="Тип"
                        value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value as CategoryType })}
                        options={CATEGORY_TYPE_OPTIONS}
                        data-testid="category-form-type"
                    />
                    <Textarea
                        label="Описание"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="category-form-description"
                    />
                    <Input
                        label="URL иконки"
                        value={form.iconUrl}
                        onChange={(e) => setForm({ ...form, iconUrl: e.target.value })}
                        placeholder="https://..."
                        error={formErrors.iconUrl}
                        data-testid="category-form-icon-url"
                    />
                    <Input
                        label="Порядок сортировки"
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                        data-testid="category-form-sort-order"
                    />
                    <Switch
                        checked={form.isActive}
                        onChange={(next) => setForm({ ...form, isActive: next })}
                        label="Активна"
                        testId="category-form-is-active"
                    />

                    {submitError !== null && submitError !== '' && (
                        <div className="text-sm text-red-600" data-testid="category-form-error">
                            {submitError}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setModalOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || isUpdating}
                            data-testid="category-form-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {editing ? 'Сохранить' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Удалить категорию"
                message={
                    pendingDelete
                        ? `Удалить категорию «${pendingDelete.name}»? Действие необратимо.`
                        : ''
                }
                confirmLabel="Удалить"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
